/**
 * Brands (migration 0087): ArcNet's, and a reseller's own for its clients' portals and email.
 * Resellers brand; clients do not (owner, 18 September 2026).
 */
import { z } from 'zod';

const optionalText = (max: number) =>
  z.string().trim().max(max).transform((v) => (v === '' ? null : v)).nullable().optional();

export const BrandUpdateSchema = z.object({
  /** A reseller's brand applies only when switched on; ArcNet's always does. */
  enabled: z.boolean().default(false),
  name: z.string().trim().min(1).max(60),
  accent: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'A colour as #rrggbb'),
  /** The header's gradient, dark to light: both or neither. */
  gradient_from: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  gradient_to: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  support_email: z.string().trim().email().max(254).nullable().optional().or(z.literal('').transform(() => null)),
  support_phone: optionalText(40),
  website: optionalText(200),
  footer: optionalText(300),
}).strict();
export type BrandUpdate = z.infer<typeof BrandUpdateSchema>;

/** A PNG or JPEG, base64, up to 256 KB. */
export const BrandLogoSchema = z.object({ data: z.string().min(16).max(360_000) }).strict();

export interface BrandSettings {
  reseller_id: string | null;
  enabled: boolean;
  name: string;
  accent: string;
  gradient_from: string | null;
  gradient_to: string | null;
  logo_url: string | null;
  support_email: string | null;
  support_phone: string | null;
  website: string | null;
  footer: string | null;
  updated_at: string | null;
}

/** What the portal shows for the signed-in person: their reseller's brand, or ArcNet's. */
export const SessionBrandSchema = z.object({
  name: z.string(),
  accent: z.string(),
  gradient_from: z.string().nullable(),
  gradient_to: z.string().nullable(),
  logo_url: z.string().nullable(),
  /** False for ArcNet's own, which the portal already looks like. */
  custom: z.boolean(),
});
export type SessionBrand = z.infer<typeof SessionBrandSchema>;
